(function () {
  'use strict';

  if (window.FlooringWorksCustomContactForm) {
    window.FlooringWorksCustomContactForm.initialize(document);
    return;
  }

  var limitRequests = Object.create(null);

  function formatMegabytes(bytes) {
    var megabytes = bytes / 1000000;
    return (Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)) + ' MB';
  }

  function formatFileSize(bytes) {
    if (bytes < 1000) {
      return bytes + (bytes === 1 ? ' byte' : ' bytes');
    }

    var divisor = bytes < 1000000 ? 1000 : 1000000;
    var unit = bytes < 1000000 ? 'KB' : 'MB';
    var value = bytes / divisor;
    return (Number.isInteger(value) ? value : value.toFixed(1)) + ' ' + unit;
  }

  function updateFileSummary(fileList, summaryElement) {
    var files = Array.prototype.slice.call(fileList || []);

    if (!files.length) {
      summaryElement.textContent = 'No images selected';
      return;
    }

    var totalBytes = files.reduce(function (sum, file) {
      return sum + file.size;
    }, 0);

    if (files.length === 1) {
      summaryElement.textContent = (files[0].name || 'Unnamed image') + ' (' + formatFileSize(totalBytes) + ')';
      return;
    }

    var filenames = files.map(function (file) {
      return file.name || 'Unnamed image';
    }).join(', ');

    summaryElement.textContent = files.length + ' images selected (' + formatFileSize(totalBytes) + '): ' + filenames;
  }

  function parseLimits(payload) {
    var limits = payload && payload.limits;
    var maxImages = Number(limits && limits.maxImages);
    var maxTotalImageBytes = Number(limits && limits.maxTotalImageBytes);

    if (!Number.isInteger(maxImages) || maxImages < 1 || !Number.isInteger(maxTotalImageBytes) || maxTotalImageBytes < 1) {
      throw new Error('The contact API returned invalid image upload limits.');
    }

    return {
      maxImages: maxImages,
      maxTotalImageBytes: maxTotalImageBytes
    };
  }

  function loadLimits(endpoint) {
    if (!limitRequests[endpoint]) {
      limitRequests[endpoint] = fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Unable to load image upload limits.');
        }
        return response.json();
      }).then(parseLimits).catch(function (error) {
        delete limitRequests[endpoint];
        throw error;
      });
    }

    return limitRequests[endpoint];
  }

  function clearStatus(statusElement) {
    statusElement.hidden = true;
    statusElement.innerHTML = '';
  }

  function showSuccess(statusElement, message) {
    var alert = document.createElement('p');
    alert.className = 'alert alert--success';
    alert.textContent = message;
    statusElement.innerHTML = '';
    statusElement.appendChild(alert);
    statusElement.hidden = false;
    statusElement.focus();
  }

  function showErrors(statusElement, messages) {
    var alert = document.createElement('div');
    var list = document.createElement('ul');
    alert.className = 'alert alert--error';
    list.className = 'alert__error-list';
    list.setAttribute('role', 'list');

    messages.filter(Boolean).forEach(function (message) {
      var item = document.createElement('li');
      item.className = 'alert__error-item';
      item.textContent = message;
      list.appendChild(item);
    });

    if (!list.children.length) {
      var fallbackItem = document.createElement('li');
      fallbackItem.className = 'alert__error-item';
      fallbackItem.textContent = 'Something went wrong. Please try again.';
      list.appendChild(fallbackItem);
    }

    alert.appendChild(list);
    statusElement.innerHTML = '';
    statusElement.appendChild(alert);
    statusElement.hidden = false;
    statusElement.focus();
  }

  function validateImages(fileList, limits) {
    var files = Array.prototype.slice.call(fileList || []);
    var errors = [];

    if (files.length > limits.maxImages) {
      errors.push('Please select no more than ' + limits.maxImages + ' images.');
    }

    var totalBytes = files.reduce(function (sum, file) {
      return sum + file.size;
    }, 0);

    if (totalBytes > limits.maxTotalImageBytes) {
      errors.push('The selected images must be ' + formatMegabytes(limits.maxTotalImageBytes) + ' or less in total.');
    }

    files.forEach(function (file) {
      if (!file.size) {
        errors.push(file.name + ' is empty.');
      } else if (file.type && file.type.indexOf('image/') !== 0) {
        errors.push(file.name + ' is not a valid image.');
      }
    });

    return errors;
  }

  function setLoading(form, submitButton, isLoading) {
    form.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    submitButton.disabled = isLoading;
  }

  function getApiErrors(payload, status) {
    if (payload && payload.fieldErrors && typeof payload.fieldErrors === 'object') {
      var fieldMessages = Object.keys(payload.fieldErrors).map(function (key) {
        return payload.fieldErrors[key];
      }).filter(Boolean);
      if (fieldMessages.length) return fieldMessages;
    }

    if (payload && payload.message) return [payload.message];
    if (status === 413) return ['The selected images are too large. Please keep their combined size within the displayed limit.'];
    return ['Something went wrong. Please try again.'];
  }

  function resetFilledStates(form) {
    form.querySelectorAll('input, textarea').forEach(function (field) {
      if (field.type !== 'file') {
        field.classList.toggle('is-filled', field.value !== '');
      }
    });

    form.querySelectorAll('select').forEach(function (select) {
      var wrapper = select.closest('.select-wrapper');
      if (wrapper) wrapper.classList.toggle('is-filled', select.value !== '');
    });
  }

  function initializeForm(form) {
    if (form.getAttribute('data-contact-form-initialized') === 'true') return;
    form.setAttribute('data-contact-form-initialized', 'true');

    var endpoint = (form.getAttribute('data-api-endpoint') || '').trim();
    var statusElement = form.querySelector('[data-contact-status]');
    var imageInput = form.querySelector('[data-contact-images]');
    var imageHelp = form.querySelector('[data-image-help]');
    var imageSummary = form.querySelector('[data-image-summary]');
    var submitButton = form.querySelector('[data-contact-submit]');
    var limitsPromise;

    updateFileSummary(imageInput.files, imageSummary);

    if (!endpoint) {
      limitsPromise = Promise.reject(new Error('The contact API endpoint is not configured.'));
    } else {
      limitsPromise = loadLimits(endpoint);
    }

    limitsPromise.then(function (limits) {
      imageHelp.textContent = 'Upload up to ' + limits.maxImages + ' images with a combined maximum size of ' + formatMegabytes(limits.maxTotalImageBytes) + '.';
    }).catch(function () {
      imageHelp.textContent = 'Image upload limits are currently unavailable.';
    });

    imageInput.addEventListener('change', function () {
      updateFileSummary(imageInput.files, imageSummary);

      limitsPromise.then(function (limits) {
        var errors = validateImages(imageInput.files, limits);
        if (errors.length) {
          showErrors(statusElement, errors);
        } else {
          clearStatus(statusElement);
        }
      }).catch(function (error) {
        showErrors(statusElement, [error.message || 'Unable to validate the selected images.']);
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      setLoading(form, submitButton, true);
      clearStatus(statusElement);

      limitsPromise.then(function (limits) {
        var imageErrors = validateImages(imageInput.files, limits);
        if (imageErrors.length) {
          throw { validationErrors: imageErrors };
        }

        return fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
      }).then(function (response) {
        return response.text().then(function (text) {
          var payload = null;
          if (text) {
            try {
              payload = JSON.parse(text);
            } catch (error) {
              payload = null;
            }
          }
          return { response: response, payload: payload };
        });
      }).then(function (result) {
        if (!result.response.ok || !result.payload || result.payload.success !== true) {
          showErrors(statusElement, getApiErrors(result.payload, result.response.status));
          return;
        }

        form.reset();
        resetFilledStates(form);
        updateFileSummary(imageInput.files, imageSummary);
        showSuccess(statusElement, result.payload.message || 'Your message has been sent successfully.');
      }).catch(function (error) {
        if (error && error.validationErrors) {
          showErrors(statusElement, error.validationErrors);
        } else {
          showErrors(statusElement, [error && error.message ? error.message : 'Something went wrong. Please try again.']);
        }
      }).finally(function () {
        setLoading(form, submitButton, false);
      });
    });
  }

  function initialize(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-custom-contact-form]').forEach(initializeForm);
  }

  window.FlooringWorksCustomContactForm = { initialize: initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initialize(document);
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });
})();
